import React, { useEffect, useRef, useState } from "react";
import { AfDataType } from "utils/TableColumnData";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { Table, TableHeader, TableBody, TableColumn, TableRow, TableCell } from "@heroui/table";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, SankeyController, Flow);
ChartJS.defaults.plugins.tooltip.callbacks.title = () => ""; // hides the ugly title

const ExpenseReport: React.FC<any> = (props) => {
  const ADT = AfDataType;
  const navigator = useNavigate();
  const [tableData, setTableData] = useState<any>({});
  const [totals, setTotals] = useState<any>({});
  const [kpsTotal, setKpsTotal] = useState<any>({});
  const [startDate, setStartDate] = useState<any>(moment().startOf("day").startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").endOf("month").format("YYYY-MM-DD"));
  const [sortStat, setSortStat] = useState<any>({});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [ackNoFilter, setAckNoFilter] = useState("");
  const sankeyRef = useRef<HTMLCanvasElement>(null);

  const [chartData, setChartData] = useState<any>({
    labels: ["Red", "Blue", "Yellow", "Green", "Purple", "Orange"],
    datasets: [
      {
        label: "",
        data: [1, 1, 1, 1, 1, 1],
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
          "rgba(75, 192, 192, 0.2)",
          "rgba(153, 102, 255, 0.2)",
          "rgba(255, 159, 64, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  });

  const [query, setQuery] = useState<any>({
    startDate: startDate ? startDate : moment().startOf("day").startOf("month"),
    endDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day").endOf("month"),
  });
  const [isDataAdded, setIsDataAdded] = useState(false);
  const [branchOpt, setBranchOtp] = useState<any>();
  const [branchOptions, setBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const [selectedStatus, setSelectedStatus] = useState<any>();

  useQuery(["EmployeeListApi"], () => ListCommanWay("/admin/user/list", { isThirdParty: false }, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.name,
          });
        });
        setBranchOptions(opt);
      }
    },
  });

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_expense_report"],
    () => ListCommanWay("/admin/companyexpense/report", { ...query }, {}),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          console.log("data.data", data.data);
          setTableData(data.data);

          const totalTax =
            data.data.totalRevenue.tax_cash +
            data.data.totalRevenue.tax_credit -
            ((data.data?.vendor?.cgst || 0) + (data.data?.vendor?.sgst || 0) + (data.data?.vendor?.igst || 0));

          const profit =
            (data.data?.vendor?.cgst || 0) +
            (data.data?.vendor?.sgst || 0) +
            (data.data?.vendor?.igst || 0) +
            data.data.totalRevenue.amount_cash +
            data.data.totalRevenue.amount_credit -
            (data.data.rent + data.data.salary + data.data.travel + data.data.vendor?.finalAmount + data.data.vouchure + data.data.other);
          setChartData({
            labels: ["Rent", "Salary", "Travel", "Vendor", "Vouchure", "Other", "Sell GST"],
            datasets: [
              {
                label: "",
                data: [
                  data.data.rent,
                  data.data.salary,
                  data.data.travel,
                  data.data.vendor?.finalAmount,
                  data.data.vouchure,
                  data.data.other,
                  totalTax,
                  profit,
                ],
                backgroundColor: [
                  "rgba(255, 99, 132, 0.8)",
                  "rgba(54, 162, 235, 0.8)",
                  "rgba(255, 206, 86, 0.8)",
                  "rgba(75, 192, 192, 0.8)",
                  "rgba(153, 102, 255, 0.8)",
                  "rgba(255, 159, 64, 0.8)",
                  "rgba(0,0,0,0.8)",
                  "rgba(21, 192, 121, 0.8)",
                ],
                borderColor: [
                  "rgba(255, 99, 132, 1)",
                  "rgba(54, 162, 235, 1)",
                  "rgba(255, 206, 86, 1)",
                  "rgba(75, 192, 192, 1)",
                  "rgba(153, 102, 255, 1)",
                  "rgba(255, 159, 64, 1)",
                  "rgba(0,0,0,1)",
                  "rgba(21, 192, 121, 1)",
                ],
                borderWidth: 1,
              },
            ],
          });
        } else {
          setTableData([]);
        }
      },
      onError: (data) => {
        setTableData([]);
      },
    }
  );

  useEffect(() => {
    setTotals({ grand: 0 });
    refetch();
  }, [query]);

  const formatINR = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);

  const __handleSubmit = async () => {
    setIsDataAdded(!isDataAdded);
    console.log(selectedStatus);
    setQuery({
      startDate: startDate ? startDate : moment().startOf("day").startOf("month"),
      endDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day").endOf("month"),
    });
  };

  const formatIndian = (num: number) => {
    if (num === 0) return "₹0";
    const abs = Math.abs(num);
    if (abs >= 1000000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (abs >= 1000) return `₹${(num / 1000).toFixed(1)} K`;
    return `₹${num.toLocaleString("en-IN")}`;
  };

  useEffect(() => {
    if (!tableData) return;

    // -----------------------------
    // PREPARE ALL NUMBERS
    // -----------------------------
    // convert amount to 2 decimal places

    const cash = parseFloat((tableData.totalRevenue?.amount_cash || 0).toFixed(2));
    const credit = parseFloat((tableData.totalRevenue?.amount_credit || 0).toFixed(2));
    const taxCash = parseFloat((tableData.totalRevenue?.tax_cash || 0).toFixed(2));
    const taxCredit = parseFloat((tableData.totalRevenue?.tax_credit || 0).toFixed(2));

    const rent = parseFloat((tableData.rent || 0).toFixed(2));
    const salary = parseFloat((tableData.salary || 0).toFixed(2));
    const voucher = parseFloat((tableData.vouchure || 0).toFixed(2));
    const other = parseFloat((tableData.other || 0).toFixed(2));

    const vendor = parseFloat((tableData.vendor?.finalAmount || 0).toFixed(2));
    /*const vendorBasic = parseFloat((tableData.vendor?.basicAmount || 0).toFixed(2));
    const vendorTax = parseFloat(
      ((tableData.vendor?.cgst || 0) + (tableData.vendor?.sgst || 0) + (tableData.vendor?.igst || 0)).toFixed(2)
    );*/


    const travel = parseFloat((tableData.travel || 0).toFixed(2));

    // Revenue
    const revenue = parseFloat((cash + credit + taxCash + taxCredit).toFixed(2));

    // Cost of Revenue
    const costOfRevenue = parseFloat((vendor + travel).toFixed(2));

    // Gross Profit
    const grossProfit = parseFloat((revenue - costOfRevenue).toFixed(2));

    // Operating Expenses
    const operatingExpenses = parseFloat((rent + salary + voucher + other).toFixed(2));

    // Operating Profit
    const operatingProfit = parseFloat((grossProfit - operatingExpenses).toFixed(2));

    // GST Payable (you can replace formula)
    const gstPayable = parseFloat((taxCash + taxCredit).toFixed(2));

    // Net Profit
    const netProfit = parseFloat((operatingProfit - gstPayable).toFixed(2));

    // -----------------------------
    // SANKEY FLOWS
    // -----------------------------
    const flows = [
      // 1. Merge into Revenue
      { from: "Credit", to: "Revenue", flow: credit },
      { from: "Cash", to: "Revenue", flow: cash },
      { from: "TaxCredit", to: "Revenue", flow: taxCredit },
      { from: "TaxCash", to: "Revenue", flow: taxCash },

      // 2. Revenue splits
      { from: "Revenue", to: "Gross Profit", flow: grossProfit },
      { from: "Revenue", to: "Cost of Revenue", flow: costOfRevenue },

      // 3. Gross Profit → Operating Expenses + Operating Profit
      { from: "Gross Profit", to: "Operating Expenses", flow: operatingExpenses },
      { from: "Gross Profit", to: "Operating Profit", flow: operatingProfit },

      { from: "Cost of Revenue", to: "Vendor", flow: vendor },
      { from: "Cost of Revenue", to: "Travel", flow: travel },

      // 4. Operating Profit → Net Profit + GST Payable
      { from: "Operating Profit", to: "Net Profit", flow: netProfit },
      { from: "Operating Profit", to: "GST", flow: gstPayable },

      
    ];

    const ctx = sankeyRef.current?.getContext("2d");
    if (!ctx) return;

    // Destroy previous chart
    if ((sankeyRef as any).current._chart) {
      (sankeyRef as any).current._chart.destroy();
    }

    // Create new Sankey
    (sankeyRef as any).current._chart = new ChartJS(ctx, {
      type: "sankey",
      data: {
        datasets: [
          {
            label: "Money Flow",
            labels: {
              Cash: "Cash - " + formatIndian(cash), // convert amount to k, lakhs, crores
              Credit: "Credit - " + formatIndian(credit),
              TaxCash: "Tax Cash - " + formatIndian(taxCash),
              TaxCredit: "Tax Credit - " + formatIndian(taxCredit),
              Revenue: "Revenue\n" + formatIndian(revenue),
              "Cost of Revenue": "Cost of Revenue\n" + formatIndian(costOfRevenue),
              "Gross Profit": "Gross Profit\n" + formatIndian(grossProfit),
              "Operating Expenses": "Operating Expenses - " + formatIndian(operatingExpenses),
              "Operating Profit": "Operating Profit\n" + formatIndian(operatingProfit),
              "GST": "GST - " + formatIndian(gstPayable),
              "Net Profit": "Net Profit\n" + formatIndian(netProfit),
              Vendor: "Vendor\n" + formatIndian(vendor),
              Travel: "Travel - " + formatIndian(travel),
            },
            column: {
              Vendor: 3,
              Travel: 3,
              "Operating Expenses": 3,
            },

            datalabels: {
              display: false,
            },
            nodeWidth: 20,
            nodePadding: 30,
            color: "black",
            font: { size: 14, weight: "bold" },
            data: flows,
            colorFrom: (c) => "lightblue",
            colorTo: (c) => "blue",
          },
        ],
      },

      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (ctx: any) => "",
              label: (ctx: any) =>
                "₹ " +
                ctx.raw.flow.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                }),
            },
          },
        },
      },
    });
  }, [tableData]);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});
  const tableRef = useRef(null);

  return (
    <>
      <h1 className="text-center mb-0 mt-1 text-lg font-semibold ">Expense Report</h1>

      <div className="px-4 border-[1px] py-2 mx-4 mt-1 rounded-md">
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-x-2 gap-y-2">
            <div className={`items-center gap-2`}>
              <label className="mb-2 text-sm font-semibold text-gray-900">Employee</label>
              <Select
                className={`${false ? "bg-gray-300" : ""} w-48 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block`}
                options={branchOptions}
                onChange={(e) => {
                  setBranchOtp(e);
                }}
                value={branchOpt}
                isClearable={true}
              />
            </div>

            <AfDateRangePicker
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              label="Start - End Date"
              startDate={startDate}
              endDate={endDate}
            />
            <button
              type="button"
              onClick={__handleSubmit}
              className="ml-2 mt-2 rounded-md px-4 py-2 text-sm font-medium text-white bg-green-500 border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 px-4 mt-2">
        <div className="px-4 text-center border-2 rounded-md font-inter p-3 border-gray-300 w-full">
          <h2 className="text-xl font-semibold mb-0">
            {
              //if startDate and endDate has same month then show month name otherwise show startDate and endDate
              moment(startDate).isSame(moment(endDate), "month")
                ? moment(startDate).format("MMMM YYYY")
                : `${moment(startDate).format("MMMM D, YYYY")} - ${moment(endDate).format("MMMM D, YYYY")}`
            }
          </h2>

          <div className="flex gap-6 px-6 mt-4">
            {/* Chart + Total */}
            <div className="rounded-md p-4 flex flex-col items-center w-[360px]">
              <div className="w-[250px] h-[250px]">
                <Doughnut
                  data={chartData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false, // hide chart legend
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.label}: ${formatINR(context.raw)}`,
                        },
                      },
                      datalabels: {
                        color: "#fff",
                        formatter: (value, context) => {
                          // Show label only for the last data point (Credit)
                          return context.dataIndex === context.dataset.data.length - 1 ? "P&L" : "";
                        },
                        font: {
                          weight: "bold",
                          size: 14,
                        },
                        anchor: "center",
                        align: "center",
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-col justify-start px-6 pb-2 min-w-[250px]">
              <div className="text-lg font-medium mb-4">
                Expense Total: &nbsp; &nbsp;
                <span className="text-2xl">
                  {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                    //chartData.datasets[0].data.reduce((a, b) => a + b, 0)
                    chartData.datasets[0].data[0] +
                      chartData.datasets[0].data[1] +
                      chartData.datasets[0].data[2] +
                      chartData.datasets[0].data[3] +
                      chartData.datasets[0].data[4] +
                      chartData.datasets[0].data[5] +
                      chartData.datasets[0].data[6]
                  )}
                </span>
              </div>

              <div>
                <Table
                  isStriped
                  isHeaderSticky
                  removeWrapper
                  className="w-full text-sm text-left text-gray-500 "
                  aria-label="collection table"
                  classNames={{
                    td: "group-data-[odd=true]/tr:before:bg-blue-100/50", // light blue
                  }}
                >
                  <TableHeader className="text-gray-700 uppercase bg-gray-50 sticky top-0">
                    <TableColumn className="text-sm px-2 py-1 text-center">ID</TableColumn>
                    <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Head</TableColumn>
                    <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Basic</TableColumn>
                    <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">GST</TableColumn>
                    <TableColumn className="text-sm px-2 py-1 text-center border-gray-400">Total</TableColumn>
                  </TableHeader>

                  <TableBody>
                    {chartData.labels.map((label: string, index: number) => (
                      <TableRow className={`text-black-900`} key={"tbl-asd-" + index}>
                        <TableCell className="text-sm px-2 py-1 text-center">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: chartData.datasets[0].borderColor[index] }} />
                        </TableCell>
                        <TableCell className="text-sm px-2 py-2 text-center border-x border-gray-400">{label}</TableCell>
                        <TableCell className="text-sm px-4 py-2 text-right border-x border-gray-400">
                          {label == "Vendor"
                            ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                                tableData?.vendor?.basicAmount || 0
                              )
                            : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                                chartData.datasets[0].data[index]
                              )}
                        </TableCell>
                        <TableCell className="text-sm px-4 py-2 text-right border-x border-gray-400">
                          {label == "Vendor"
                            ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                                (tableData?.vendor?.cgst || 0) + (tableData?.vendor?.sgst || 0) + (tableData?.vendor?.igst || 0)
                              )
                            : "-"}
                        </TableCell>
                        <TableCell className="text-sm px-4 py-2 text-right  border-gray-400">
                          {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(chartData.datasets[0].data[index])}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="border-l pl-4">
              {/* Legend */}
              <div className="flex flex-col justify-center px-6 pb-2 min-w-[250px]">
                <div className="text-lg font-medium mb-2">
                  Revenue: &nbsp; &nbsp;
                  <span className="text-2xl">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.totalRevenue?.amount_cash || 0) +
                        (tableData?.totalRevenue?.amount_credit || 0) +
                        (tableData?.totalRevenue?.tax_cash || 0) +
                        (tableData?.totalRevenue?.tax_credit || 0)
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="">Cash Base</span>
                  </div>
                  <span className="font-semibold font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      tableData?.totalRevenue?.amount_cash || 0
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="">Cash GST</span>
                  </div>
                  <span className="font-semibold font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(tableData?.totalRevenue?.tax_cash || 0)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="">Cash Total</span>
                  </div>
                  <span className="font-bold underline font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.totalRevenue?.tax_cash || 0) + (tableData?.totalRevenue?.amount_cash || 0)
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 text-sm border-t-2 pt-2">
                  <div className="flex items-center gap-2">
                    <span className="">Credit Base</span>
                  </div>
                  <span className="font-semibold font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      tableData?.totalRevenue?.amount_credit || 0
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="">Credit GST</span>
                  </div>
                  <span className="font-semibold font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      tableData?.totalRevenue?.tax_credit || 0
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="">Credit Total</span>
                  </div>
                  <span className="font-extrabold underline font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.totalRevenue?.tax_credit || 0) + (tableData?.totalRevenue?.amount_credit || 0)
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-2 p-2 border-t-3 border-primary bg-primary/10">
                  <div className="flex items-center gap-2 mr-4">
                    <span className="">P&L (inc. GST)</span>
                  </div>
                  <span className="font-semibold underline font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.totalRevenue?.tax_cash || 0) +
                        (tableData?.totalRevenue?.amount_cash || 0) +
                        (tableData?.totalRevenue?.tax_credit || 0) +
                        (tableData?.totalRevenue?.amount_credit || 0) -
                        ((tableData?.rent || 0) +
                          (tableData?.salary || 0) +
                          (tableData?.travel || 0) +
                          (tableData?.vendor?.finalAmount || 0) +
                          (tableData?.vouchure || 0) +
                          (tableData?.other || 0))
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 text-sm border-t-2 p-2">
                  <div className="flex items-center gap-2">
                    <span className="">GST Credit</span>
                  </div>
                  <span className="font-semibold font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.vendor?.cgst || 0) + (tableData?.vendor?.sgst || 0) + (tableData?.vendor?.igst || 0)
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 p-2 border-t-3 border-primary bg-red-500/20">
                  <div className="flex items-center gap-2 mr-4">
                    <span className="">- GST</span>
                  </div>
                  <span className="font-semibold underline font-mono">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.totalRevenue?.tax_cash || 0) +
                        (tableData?.totalRevenue?.tax_credit || 0) -
                        ((tableData?.vendor?.cgst || 0) + (tableData?.vendor?.sgst || 0) + (tableData?.vendor?.igst || 0))
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 p-2 border-t-3 border-primary bg-green-500/30">
                  <div className="flex items-center gap-2 mr-4">
                    <span className="font-semibold text-xl">P&L</span>
                  </div>
                  <span className="font-semibold underline font-mono text-xl">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
                      (tableData?.vendor?.cgst || 0) +
                        (tableData?.vendor?.sgst || 0) +
                        (tableData?.vendor?.igst || 0) +
                        (tableData?.totalRevenue?.amount_cash || 0) +
                        (tableData?.totalRevenue?.amount_credit || 0) -
                        ((tableData?.rent || 0) +
                          (tableData?.salary || 0) +
                          (tableData?.travel || 0) +
                          (tableData?.vendor?.finalAmount || 0) +
                          (tableData?.vouchure || 0) +
                          (tableData?.other || 0))
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="">
            <canvas ref={sankeyRef} className="w-full h-96" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseReport;
